import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { gsap } from 'gsap';
import FeaturesBgImg from "../../assets/img/website-images/Our-best-features-pharmacy.jpg"

/* ─── Features Data ────────────────────────────────────────────── */
const features = [
    {
        icon: 'assets/img/icon/fea-icon01.png',
        title: 'Expert Faculty',
        description:
            'Learn from highly qualified professors, PhD scholars, and industry veterans who bring both academic depth and real-world pharmaceutical experience into the classroom.',
    },
    {
        icon: 'assets/img/icon/fea-icon02.png',
        title: 'Comprehensive B.Pharm Program',
        description:
            'Our four-year, eight-semester B.Pharm curriculum is RGUHS-affiliated and PCI-approved, designed to build strong foundations in pharmaceutical sciences from day one.',
    },
    {
        icon: 'assets/img/icon/fea-icon03.png',
        title: 'Hands-On Lab Training',
        description:
            'State-of-the-art laboratories for Pharmaceutics, Pharmacology, Pharmaceutical Chemistry, and Pharmacognosy ensure every student gains practical, industry-ready skills.',
    },
];

/* ─── Variants ─────────────────────────────────────────────────── */
const fadeDown = {
    hidden: { opacity: 0, y: -45 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
    },
};

const fadeInRight = {
    hidden: { opacity: 0, x: 80 },
    visible: {
        opacity: 1,
        x: 0,
        transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.2 },
    },
};

const listContainerVariants = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.2, delayChildren: 0.3 } },
};

const listItemVariants = {
    hidden: { opacity: 0, x: -50, scale: 0.97 },
    visible: {
        opacity: 1,
        x: 0,
        scale: 1,
        transition: { duration: 0.65, ease: [0.22, 1, 0.36, 1] },
    },
};

/* ─── Single Feature Item ──────────────────────────────────────── */
function FeatureItem({ feature }) {
    const iconRef = useRef(null);
    const { ref: inViewRef, inView } = useInView({ threshold: 0.3, triggerOnce: true });

    /* GSAP: icon rotates & pulses once on entry */
    useEffect(() => {
        if (inView && iconRef.current) {
            gsap.fromTo(
                iconRef.current,
                { rotation: -15, scale: 0.6, opacity: 0 },
                {
                    rotation: 0,
                    scale: 1,
                    opacity: 1,
                    duration: 0.7,
                    ease: 'back.out(1.8)',
                }
            );
        }
    }, [inView]);

    const setRefs = (el) => {
        iconRef.current = el;
        inViewRef(el);
    };

    return (
        <motion.li variants={listItemVariants}>
            <div className="step-box">
                <div className="dnumber">
                    <div className="date-box">
                        <img ref={setRefs} src={feature.icon} alt="icon" style={{ opacity: 0 }} />
                    </div>
                </div>
                <div className="text">
                    <motion.h3
                        initial={{ opacity: 0, y: 10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.5 }}
                        transition={{ duration: 0.5, delay: 0.1 }}
                    >
                        {feature.title}
                    </motion.h3>
                    <motion.p
                        initial={{ opacity: 0, y: 10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.5 }}
                        transition={{ duration: 0.5, delay: 0.2 }}
                    >
                        {feature.description}
                    </motion.p>
                </div>
            </div>
        </motion.li>
    );
}

/* ─── Main Component ───────────────────────────────────────────── */
function Shep() {
    /* Left column (title + list) */
    const { ref: leftRef, inView: leftInView } = useInView({ threshold: 0.2, triggerOnce: true });

    /* Right column (image) */
    const { ref: rightRef, inView: rightInView } = useInView({ threshold: 0.2, triggerOnce: true });

    /* GSAP: floating decorative bg image */
    const bgRef = useRef(null);
    useEffect(() => {
        if (bgRef.current) {
            gsap.to(bgRef.current, {
                y: -20,
                duration: 4,
                ease: 'sine.inOut',
                repeat: -1,
                yoyo: true,
            });
        }
    }, []);

    /* GSAP: image subtle entrance scale */
    const imgRef = useRef(null);
    useEffect(() => {
        if (rightInView && imgRef.current) {
            gsap.fromTo(
                imgRef.current,
                { scale: 0.88, opacity: 0 },
                { scale: 1, opacity: 1, duration: 1.0, ease: 'power3.out', delay: 0.2 }
            );
        }
    }, [rightInView]);

    return (
        <section
            className="steps-area p-relative ss"
            style={{ backgroundColor: 'var(--color-primary)' }}
        >
            {/* Floating decorative bg */}
            <div className="animations-10" ref={bgRef}>
                <img src="/assets/img/bg/an-img-10.png" alt="an-img-01" />
            </div>

            <div className="container">
                <div className="row align-items-center">

                    {/* ── Left Column ── */}
                    <div className="col-lg-6 col-md-12" ref={leftRef}>

                        {/* Section heading */}
                        <motion.div
                            className="section-title mb-35"
                            variants={fadeDown}
                            initial="hidden"
                            animate={leftInView ? 'visible' : 'hidden'}
                        >
                            <h2>Our Best Features</h2>
                            <p>
                                At Akash Institute of Pharmacy, we combine academic excellence with
                                practical training to prepare students for a successful career in
                                pharmaceutical sciences.
                            </p>
                        </motion.div>

                        {/* Feature list — staggered */}
                        <motion.ul
                            className="pr-20"
                            variants={listContainerVariants}
                            initial="hidden"
                            animate={leftInView ? 'visible' : 'hidden'}
                        >
                            {features.map((feature) => (
                                <FeatureItem key={feature.icon} feature={feature} />
                            ))}
                        </motion.ul>
                    </div>

                    {/* ── Right Column — Image ── */}
                    <div className="col-lg-6 col-md-12" ref={rightRef}>
                        <motion.div
                            className="step-img"
                            variants={fadeInRight}
                            initial="hidden"
                            animate={rightInView ? 'visible' : 'hidden'}
                        >
                            <motion.img
                                ref={imgRef}
                                src={FeaturesBgImg}
                                alt="class image"
                                style={{ opacity: 0 }}   /* GSAP reveals this */
                                whileHover={{ scale: 1.03 }}
                                transition={{ duration: 0.4 }}
                            />
                        </motion.div>
                    </div>

                </div>
            </div>
        </section>
    );
}

export default Shep;
